import { type PropsWithChildren } from 'react';
import { useTranslation } from 'react-i18next';
import { WALL_TYPE } from '@cx-sdk/constants';
import { type ComponentWall } from '@cx-sdk/types';
import { LockIcon } from '@cx-web/components/primitives/icons';
import { useAuthModal, VIEW_LOGIN, VIEW_SIGNUP } from '@cx-web/feature-myoc-login/provider';
import { type RepubIs } from '@cx-web/utils';

import { Button } from '~/components/cxy/cxy.ts';
import { IframeMessageType } from '~/pages/gz/types';

import styles from './styles/FiltersWall.module.css';

export type FiltersWallProps = {
  currentPath: string;
  isDisabled: boolean;
  isLoggedIn: boolean;
  repubIs: RepubIs;
  repubPath: string;
  wallType?: ComponentWall['type'];
} & PropsWithChildren;

export function FiltersWall({
  children,
  currentPath,
  isDisabled,
  isLoggedIn,
  repubIs,
  repubPath,
  wallType,
}: FiltersWallProps) {
  const { openAtView } = useAuthModal();
  const { t } = useTranslation();
  if (!isDisabled) return children;

  const isPaywall = wallType === WALL_TYPE.PAYWALL;

  const handleLoginClick = (e: React.MouseEvent) => {
    e.preventDefault();

    if (repubIs.GZ) return window.parentIFrame?.sendMessage({ type: IframeMessageType.OPEN_AUTH_MODAL });
    openAtView(VIEW_LOGIN);
  };

  return (
    <div className={styles.filtersWall}>
      <div className={styles.contentWrapper} data-testid="filters-wall">
        <LockIcon className={styles.svg} />
        <div className={styles.textWrapper}>
          {isPaywall ? (
            <Button
              className={styles.ghostButton}
              onClick={e => {
                e.preventDefault();
                window.location.href = `${repubPath}/plus?redirectTo=${currentPath}#plans`;
              }}
              variant="ghost"
            >
              {t('CX_PLUS_SUBSCRIBE')}
            </Button>
          ) : (
            <>
              <Button
                className={styles.ghostButton}
                onClick={e => {
                  e.preventDefault();
                  openAtView(VIEW_LOGIN);
                }}
                variant="ghost"
              >
                {t('CX_AUTH_LOGIN')}
              </Button>
              <span>or</span>
              <Button
                className={styles.ghostButton}
                onClick={e => {
                  e.preventDefault();
                  openAtView(VIEW_SIGNUP);
                }}
                variant="ghost"
              >
                {t('CX_AUTH_SIGNUP')}
              </Button>
            </>
          )}
          <span>{t('CX_PLUS_TO_UNLOCK_FILTERS')}</span>
          {!isLoggedIn && (
            <>
              <br />
              {isPaywall ? (
                <>
                  <span>{t('CX_PLUS_ALREADY_SUBSCRIBED')} </span>
                  <Button className={styles.ghostButton} onClick={handleLoginClick} variant="ghost">
                    {t('CX_AUTH_LOGIN')}
                  </Button>
                </>
              ) : (
                <span>Get a week free.</span>
              )}
            </>
          )}
        </div>
      </div>
      <div className={isDisabled ? styles.fadedContent : ''} data-testid="filters-content">
        {children}
      </div>
    </div>
  );
}
